import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion, AccordionSummary, AccordionDetails, Alert, Box, Button, Checkbox,
  Chip, CircularProgress, FormControlLabel, MenuItem, Select, Stack, Switch,
  TextField, Typography, Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DevicesIcon from '@mui/icons-material/Devices';
import {
  getAlarmConfig, getActivityPolicy, getFeatureAssignments, setFeatureAssignments,
  getDevices, getLocations,
} from '../api/client';
import InfoTooltip from '../components/InfoTooltip';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChanges';

// All 6 features Supervisor Device Assignment covers — Detection and
// Activity Monitoring follow EXACTLY the same deployment rules (same
// assignment table, same API shape, same UI component below), only the
// read-only "current configuration" panel differs per feature kind.
const FEATURES = [
  { key: 'drowsiness',      label: 'Drowsiness',      emoji: '😴', kind: 'alarm' },
  { key: 'yawning',         label: 'Yawning',         emoji: '🥱', kind: 'alarm' },
  { key: 'distracted',      label: 'Distracted',      emoji: '👀', kind: 'alarm' },
  { key: 'presence',        label: 'Presence',        emoji: '📍', kind: 'alarm' },
  { key: 'screenshot',      label: 'Screenshot',      emoji: '📷', kind: 'activity' },
  { key: 'browser_history', label: 'Browser History', emoji: '🌐', kind: 'activity' },
];

// ── Read-only configuration section ─────────────────────────────────────────
function ReadOnlyConfig({ feature, config }) {
  const row = (label, value) => (
    <Stack direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={600}>{value}</Typography>
    </Stack>
  );

  if (feature.kind === 'activity') {
    const enabledKey = `${feature.key}_enabled`;
    return (
      <Box sx={{ p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          Configuration (Read Only)
        </Typography>
        <FormControlLabel control={<Switch checked={!!config[enabledKey]} disabled />} label="Feature Enabled" />
        <Divider sx={{ my: 1 }} />
        {feature.key === 'screenshot' ? (
          <>
            {row('Interval Mode', config.screenshot_interval_mode || 'fixed')}
            {row('Interval', `${config.screenshot_interval_minutes ?? '—'} min`)}
            {row('Quality', `${config.screenshot_quality ?? '—'}%`)}
          </>
        ) : (
          row('Sync Interval', `${config.browser_sync_interval_minutes ?? '—'} min`)
        )}
        {row('Retention', `${config.retention_days ?? '—'} days`)}
        {row('Configuration Version', `v${config.config_version ?? 0}`)}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Only a vendor administrator can change these values, in Work Activity Monitoring.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        Configuration (Read Only)
      </Typography>
      <Stack direction="row" spacing={1} mb={1}>
        <FormControlLabel control={<Switch checked={!!config.is_enabled} disabled />} label="Feature Enabled" />
        <FormControlLabel control={<Switch checked={!!config.alarm_enabled} disabled />} label="Alarm Enabled" />
      </Stack>
      <Divider sx={{ mb: 1 }} />
      {row('Delay', `${config.trigger_delay_seconds}s`)}
      {row('Cooldown', `${config.cooldown_seconds}s`)}
      {row('Repeat Alarm', config.repeat_enabled ? 'On' : 'Off')}
      {row('Volume', `${config.volume_percent}%`)}
      {row('Audio Name', config.audio?.original_filename || 'Default sound')}
      {row('Audio Version', config.audio ? `v${config.audio.version}` : '—')}
      {row('Last Updated', config.updated_at ? new Date(config.updated_at).toLocaleString() : '—')}
      {row('Configured By', config.updated_by || '—')}
      {row('Configuration Version', `v${config.config_version}`)}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Only a vendor administrator can change these values, in Alarm Configuration.
      </Typography>
    </Box>
  );
}

// ── Editable device-assignment section — identical for every feature kind,
// Detection or Activity Monitoring: same table, same API, same component. ──
function DeviceAssignment({ feature, devices, locations, onSaved }) {
  const [assignedIds, setAssignedIds] = useState(new Set());
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [assignedFilter, setAssignedFilter] = useState('');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [initialIds, setInitialIds] = useState(new Set());

  useEffect(() => {
    getFeatureAssignments(feature.key)
      .then(rows => {
        const ids = new Set(rows.map(r => r.device_id));
        setAssignedIds(ids); setInitialIds(ids); setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [feature.key]);

  const toggle = (deviceId) => {
    setAssignedIds(prev => {
      const next = new Set(prev);
      if (next.has(deviceId)) next.delete(deviceId); else next.add(deviceId);
      return next;
    });
    setDirty(true);
  };

  const filtered = devices.filter(d => {
    if (search && !(d.hostname || d.id).toLowerCase().includes(search.toLowerCase())) return false;
    if (locationFilter && d.location_id !== locationFilter) return false;
    if (statusFilter === 'online' && !d.is_online) return false;
    if (statusFilter === 'offline' && d.is_online) return false;
    if (assignedFilter === 'assigned' && !assignedIds.has(d.id)) return false;
    if (assignedFilter === 'unassigned' && assignedIds.has(d.id)) return false;
    return true;
  });

  const assignAll = () => {
    setAssignedIds(prev => {
      const next = new Set(prev);
      filtered.forEach(d => next.add(d.id));
      return next;
    });
    setDirty(true);
  };
  const removeAll = () => {
    setAssignedIds(prev => {
      const next = new Set(prev);
      filtered.forEach(d => next.delete(d.id));
      return next;
    });
    setDirty(true);
  };
  // "Default" under the opt-out model (see services/feature_config.py::
  // auto_assign_all_devices) is every vendor device assigned — this button
  // discards any local opt-outs and returns to that default, same as what
  // happens automatically the moment the vendor admin (re-)enables the
  // feature.
  const resetToDefault = () => {
    setAssignedIds(new Set(devices.map(d => d.id)));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      await setFeatureAssignments(feature.key, Array.from(assignedIds));
      setSuccess('Assignments saved. Edge devices pick up the change within seconds — no restart, no reconfiguration.');
      setInitialIds(new Set(assignedIds));
      setDirty(false);
      onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save assignments');
    } finally {
      setSaving(false);
    }
  };

  const discard = () => { setAssignedIds(initialIds); setDirty(false); };
  const { confirmNavigation } = useUnsavedChangesGuard(dirty, { onSave: save, onDiscard: discard });
  const handleCancel = () => confirmNavigation(discard);

  if (!loaded) return <CircularProgress size={20} />;

  const assignedCount = Array.from(assignedIds).filter(id => devices.some(d => d.id === id)).length;

  return (
    <Box sx={{ p: 1.5 }}>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        Assigned Devices — {assignedCount} / {devices.length}
        <InfoTooltip description="Devices that will receive alarms for this feature." saveRequired scope="future" />
        {dirty && <Chip size="small" color="warning" variant="outlined" label="Unsaved Changes" sx={{ ml: 1 }} />}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Stack direction="row" spacing={1} mb={1} flexWrap="wrap">
        <TextField size="small" placeholder="Search devices" value={search}
                  onChange={e => setSearch(e.target.value)} sx={{ minWidth: 180 }} />
        <Select size="small" value={locationFilter} displayEmpty sx={{ minWidth: 160 }}
                onChange={e => setLocationFilter(e.target.value)}>
          <MenuItem value="">All locations</MenuItem>
          {locations.map(l => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
        </Select>
        <Select size="small" value={statusFilter} displayEmpty sx={{ minWidth: 140 }}
                onChange={e => setStatusFilter(e.target.value)}>
          <MenuItem value="">Online + Offline</MenuItem>
          <MenuItem value="online">Online only</MenuItem>
          <MenuItem value="offline">Offline only</MenuItem>
        </Select>
        <Select size="small" value={assignedFilter} displayEmpty sx={{ minWidth: 140 }}
                onChange={e => setAssignedFilter(e.target.value)}>
          <MenuItem value="">Assigned + Unassigned</MenuItem>
          <MenuItem value="assigned">Assigned only</MenuItem>
          <MenuItem value="unassigned">Unassigned only</MenuItem>
        </Select>
        <Button size="small" onClick={assignAll}>Assign All</Button>
        <Button size="small" onClick={removeAll}>Remove All</Button>
        <Button size="small" onClick={resetToDefault}>Reset to Default</Button>
      </Stack>

      <Box sx={{ maxHeight: 260, overflowY: 'auto', border: '1px solid rgba(0,0,0,0.1)', borderRadius: 1 }}>
        {filtered.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>No devices match.</Typography>
        ) : filtered.map(d => (
          <Stack key={d.id} direction="row" alignItems="center" spacing={1}
                sx={{ px: 1, py: 0.5, borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
            <Checkbox size="small" checked={assignedIds.has(d.id)} onChange={() => toggle(d.id)} />
            <Typography variant="body2" sx={{ flexGrow: 1 }}>{d.hostname || d.id}</Typography>
            <Chip size="small" label={d.is_online ? 'Online' : 'Offline'}
                  color={d.is_online ? 'success' : 'default'} variant="outlined" />
            {d.current_operator && (
              <Typography variant="caption" color="text.secondary">👤 {d.current_operator.name}</Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              {d.last_seen_at ? new Date(d.last_seen_at).toLocaleString() : 'Never seen'}
            </Typography>
          </Stack>
        ))}
      </Box>

      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
        <Button onClick={handleCancel} disabled={!dirty || saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={!dirty || saving}>
          {saving ? <CircularProgress size={16} /> : 'Save Assignments'}
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        Saving only updates which devices run this feature — it never changes the configuration above.
      </Typography>
    </Box>
  );
}

function FeatureAssignmentCard({ feature, config, devices, locations, onReload }) {
  const isEnabled = feature.kind === 'activity' ? !!config[`${feature.key}_enabled`] : !!config.is_enabled;
  return (
    <Accordion defaultExpanded={feature.key === 'drowsiness'}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%', pr: 2 }}>
          <Typography fontSize="1.4rem">{feature.emoji}</Typography>
          <Typography fontWeight={700} sx={{ flexGrow: 1 }}>{feature.label}</Typography>
          <Chip size="small" label={isEnabled ? 'Enabled' : 'Off'}
                color={isEnabled ? 'success' : 'default'} />
          {feature.kind === 'alarm' && isEnabled && (
            <Chip size="small" label={config.alarm_enabled ? 'Alarm on' : 'Silent'}
                  color={config.alarm_enabled ? 'success' : 'warning'} variant="outlined" />
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <ReadOnlyConfig feature={feature} config={config} />
        <DeviceAssignment feature={feature} devices={devices} locations={locations} onSaved={onReload} />
      </AccordionDetails>
    </Accordion>
  );
}

export default function AlarmAssignmentPage() {
  const [alarmConfigs, setAlarmConfigs] = useState(null);
  const [activityPolicy, setActivityPolicy] = useState(null);
  const [devices, setDevices] = useState([]);
  const [locations, setLocations] = useState([]);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    getAlarmConfig().then(setAlarmConfigs).catch(e => setError(e.response?.data?.detail || 'Unable to load alarm configuration'));
    getActivityPolicy().then(setActivityPolicy).catch(() => setActivityPolicy(null));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    getDevices().then(setDevices).catch(() => setDevices([]));
    getLocations().then(setLocations).catch(() => setLocations([]));
  }, []);

  const configFor = (feature) => (feature.kind === 'activity' ? activityPolicy : alarmConfigs?.[feature.key]);
  const available = FEATURES.filter(f => configFor(f));
  const loaded = alarmConfigs !== null || activityPolicy !== null;

  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <DevicesIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>Device Assignment</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configuration is set by your vendor administrator and shown here read-only.
        Assign which of your devices actually run each feature — Detection, Screenshot,
        and Browser History all deploy the same way. Assignment changes apply
        automatically, no device restart and no reconfiguration.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loaded ? <CircularProgress /> : available.length === 0 ? (
        <Alert severity="info">No features are licensed for your vendor account yet.</Alert>
      ) : (
        <Stack spacing={1}>
          {available.map(feature => (
            <FeatureAssignmentCard
              key={feature.key}
              feature={feature}
              config={configFor(feature)}
              devices={devices}
              locations={locations}
              onReload={load}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}
